import { Grid, Link, Typography } from '@mui/material';
import {
    CheckboxElement,
    FormContainer,
    PasswordElement,
    PasswordRepeatElement,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import { useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import AuthRequestStatus from '../../../enums/AuthRequestStatus';
import { CreateAccountRequest } from '../../../sic-lib/api/createAccount/types/createAccountRequest';
import { useCreateAccount } from '../../../sic-lib/api/hooks/useCreateAccount';
import { CREATE_ACCOUNT_FORM_DEFAULTS } from '../../../sic-lib/defaults';
import { useAppDispatch } from '../../../store/hooks';
import { updateRequestStatus } from '../../../store/users';
import AxiosErrorAlert from '../Alerts/AxiosErrorAlert';
import ButtonLoading from '../ButtonLoading';
import { CountriesAutocompleteElement } from '../FormControls/Inputs/CountriesAutocompleteElement';
import PhoneNumberTextFieldElement from '../FormControls/Inputs/PhoneNumberTextFieldElement';
import { StatesProvinceAutocompleteElement } from '../FormControls/Inputs/StatesProvinceAutocompleteElement';

export default function FormRegister() {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    let { offerKey } = useParams<{ offerKey: string }>();

    const formContext = useForm<CreateAccountRequest>({
        defaultValues: CREATE_ACCOUNT_FORM_DEFAULTS,
        mode: 'all',
    });

    const {
        data: createAccountData,
        mutate: createAccountMutation,
        isLoading: isCreateAccountLoading,
        isSuccess: isCreateAccountSuccess,
        isError: isCreateAccountError,
        error: createAccountError,
    } = useCreateAccount();

    const handleSubmit = (account: CreateAccountRequest) => {
        createAccountMutation(
            { ...account, userName: account.email, offerKey: offerKey },
            {
                onSuccess(data, variables, context) {
                    // TODO: Handle success
                    console.log('onSuccess', data, variables, context);
                    formContext.reset();
                    dispatch(updateRequestStatus(AuthRequestStatus.authSuccess));
                },
                onError(data, variables, context) {},
            },
        );
    };

    return (
        <FormContainer formContext={formContext} onSuccess={handleSubmit}>
            <Grid item container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h4" textAlign="center" mb={2}>
                        Create Account
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextFieldElement label="First Name" name="firstName" required fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextFieldElement label="Last Name" name="lastName" required fullWidth />
                </Grid>
                {/* <Grid item width="100%">
                        <TextFieldElement
                            label="Username"
                            name="userName"
                            required
                            fullWidth
                            validation={{
                                minLength: { value: 3, message: 'Minimum length is 3' },
                                maxLength: { value: 250, message: 'Maximum length is 250' },
                                pattern: {
                                    value: /^[a-zA-Z0-9-._@+]+$/,
                                    message:
                                        'Must have one of the following; lower and upper case letter, number and special character: -._@+',
                                },
                            }}
                        />
                    </Grid> */}
                <Grid item xs={12}>
                    <PhoneNumberTextFieldElement
                        label="Phone Number"
                        name="phone"
                        required
                        fullWidth
                        validation={{
                            maxLength: { value: 30, message: 'Phone is too long' },
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextFieldElement type="email" label="Email" name="email" required fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <PasswordElement
                        label="Password"
                        name="password"
                        autoComplete="new-password"
                        required
                        fullWidth
                        validation={{
                            minLength: {
                                value: 6,
                                message: 'Your Password must be at least 6 character long.',
                            },
                            maxLength: { value: 250, message: 'Password is too long' },
                            pattern: {
                                value: /^(?=.*\d).{6,}$/,
                                message:
                                    'Your password needs to be at least 6 characters long and must include at least one number.',
                            },
                        }}
                        helperText="Your password needs to be at least 6 characters long and must include at least one number."
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <PasswordRepeatElement
                        label="Confirm Password"
                        name="verifyPassword"
                        passwordFieldName="password"
                        validation={{
                            maxLength: { value: 250, message: 'Password is too long' },
                        }}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6}>
                    <CountriesAutocompleteElement name="country" label="Country" required />
                </Grid>
                <Grid item xs={6}>
                    <StatesProvinceAutocompleteElement
                        name="stateProvince"
                        label="State/Province"
                        required
                    />
                </Grid>
                <Grid item width="100%">
                    <TextFieldElement
                        label="Zip/Postal Code"
                        name="zipPostalCode"
                        validation={{
                            maxLength: { value: 12, message: 'Zip/Postal code is too long' },
                        }}
                        required
                        fullWidth
                    />
                </Grid>
                <Grid item width="100%">
                    <CheckboxElement
                        name="agreedToTerms"
                        label={
                            <Typography component="p" variant="caption" align="center">
                                I agree with StayInContact{' '}
                                <Link
                                    href="https://stayincontact.com/eula"
                                    target="blank"
                                    // className={classes.linkText}
                                >
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link
                                    href="https://consulnet.com/privacy"
                                    target="blank"
                                    // className={classes.linkText}
                                >
                                    Privacy Policy
                                </Link>
                            </Typography>
                        }
                        required
                    />
                </Grid>
                {isCreateAccountError && (
                    <Grid item>
                        <AxiosErrorAlert error={createAccountError} />
                    </Grid>
                )}
                <Grid item width="100%">
                    <ButtonLoading
                        type="submit"
                        color="primary"
                        variant="contained"
                        size="large"
                        isLoading={isCreateAccountLoading}
                        isSuccess={isCreateAccountSuccess}
                        successLabel="Account Created"
                        isError={isCreateAccountError}
                        disabled={!formContext.formState.isValid}
                        fullWidth
                    >
                        Create Account
                    </ButtonLoading>
                </Grid>
            </Grid>
        </FormContainer>
    );
}

const useStyles = makeStyles()({});
